import React, { useState, useEffect } from 'react';
import { updateShift, createShift, deleteShift, Shift } from '../../services/rosterService';
import apiClient from '../../services/api';
import { format, parseISO } from 'date-fns';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { DatePicker } from '../ui/DatePicker';

interface EditShiftModalProps {
    shift: Shift | null;
    staffId?: string;
    date?: string;
    versionId?: string;
    onClose: () => void;
    onSuccess?: (action: 'create' | 'update' | 'delete', data?: any) => void;
    allowOverride?: boolean; // Allow admin to override constraints
}

interface User {
    id: string;
    name: string;
    email: string;
    role: {
        name: string;
    };
    userSkills?: Array<{
        skill: {
            name: string;
        };
    }>;
}

const EditShiftModal: React.FC<EditShiftModalProps> = ({ shift, staffId, date, versionId, onClose, onSuccess, allowOverride = true }) => {
    const isEditing = !!shift;
    const [formData, setFormData] = useState({
        userId: shift?.userId || staffId || '',
        date: shift ? format(parseISO(shift.startTime), 'yyyy-MM-dd') : (date ? format(parseISO(date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')),
        startTime: shift ? format(parseISO(shift.startTime), 'HH:mm') : '07:00',
        endTime: shift ? format(parseISO(shift.endTime), 'HH:mm') : '19:00',
        type: shift?.type || '',
        location: (shift as any)?.location || '',
        notes: shift?.notes || '',
    });
    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [error, setError] = useState('');
    const [warnings, setWarnings] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [overrideConstraints, setOverrideConstraints] = useState(false);
    const [constraintViolations, setConstraintViolations] = useState<string[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                const response = await apiClient.get('/users', {
                    params: { limit: 100 },
                });
                setUsers(response.data.users || response.data.data || []);
            } catch (error) {
                console.error('Failed to load users', error);
            } finally {
                setLoadingUsers(false);
            }
        };
        loadUsers();
    }, []);

    useEffect(() => {
        // Validate skills when user or location changes
        if (formData.userId && formData.location) {
            validateSkills();
        }
    }, [formData.userId, formData.location, formData.type]);

    const validateSkills = async () => {
        const warningsList: string[] = [];
        const violationsList: string[] = [];
        const selectedUser = users.find(u => u.id === formData.userId);

        if (selectedUser && formData.location) {
            const userSkills = new Set(selectedUser.userSkills?.map(us => us.skill.name) || []);

            if (formData.location === 'Heathrow' && formData.type === 'CLEARER') {
                if (!userSkills.has('LHR full Pass')) {
                    violationsList.push('User must have "LHR full Pass" skill');
                }
                if (!userSkills.has('can drive')) {
                    violationsList.push('User must have "can drive" skill');
                }
            } else if (formData.location === 'LGW' && formData.type === 'CLEARER') {
                if (!userSkills.has('LGW full pass')) {
                    violationsList.push('User must have "LGW full pass" skill');
                }
                if (!userSkills.has('can drive')) {
                    violationsList.push('User must have "can drive" skill');
                }
            }
        }

        setWarnings(warningsList);
        setConstraintViolations(violationsList);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Check for constraint violations if override not enabled
        if (constraintViolations.length > 0 && !overrideConstraints) {
            setError('Please resolve constraint violations or enable override to proceed');
            return;
        }

        setSaving(true);

        try {
            if (!isEditing && !versionId) {
                setError('Create or select a draft roster for this calendar month before adding shifts.');
                return;
            }

            const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
            const endDateTime = new Date(`${formData.date}T${formData.endTime}`);

            const shiftData: any = {
                userId: formData.userId,
                startTime: startDateTime.toISOString(),
                endTime: endDateTime.toISOString(),
                type: formData.type,
                location: formData.location || undefined,
                notes: formData.notes || undefined,
            };

            // Add override flag if constraints were overridden
            if (overrideConstraints && constraintViolations.length > 0) {
                shiftData.overrideConstraints = true;
                shiftData.overrideReason = 'Admin override';
            }

            if (isEditing && shift) {
                await updateShift(shift.id, shiftData);
                if (onSuccess) {
                    onSuccess('update', { ...shift, ...formData, startTime: startDateTime.toISOString(), endTime: endDateTime.toISOString() });
                }
            } else {
                await createShift({
                    ...shiftData,
                    versionId,
                });
                if (onSuccess) {
                    onSuccess('create', { ...formData, startTime: startDateTime.toISOString(), endTime: endDateTime.toISOString() });
                }
            }
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to save shift');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!shift) return;

        try {
            await deleteShift(shift.id);
            setShowDeleteConfirm(false);
            if (onSuccess) {
                onSuccess('delete', shift);
            }
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to delete shift');
        }
    };

    // Compute allowed shift types based on selected user's skills
    const ALL_SHIFT_TYPES = [
        { value: 'LHR', label: 'LHR Clearer (Heathrow)' },
        { value: 'CLEARER', label: 'LGW Clearer (Gatwick)' },
        { value: 'CONTROL', label: 'Control' },
        { value: 'OFFICE', label: 'Office' },
        { value: 'WAREHOUSE', label: 'Warehouse' },
    ];

    const getShiftTypesForUser = (user: User | undefined) => {
        if (!user || !user.userSkills || user.userSkills.length === 0) {
            return ALL_SHIFT_TYPES;
        }
        const skillNames = user.userSkills.map(us => us.skill.name.toLowerCase());
        const allowed: typeof ALL_SHIFT_TYPES = [];

        if (skillNames.some(s => s.includes('lhr full pass') || s.includes('agency full pass'))) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'LHR')!);
        }
        if (skillNames.some(s => s.includes('lgw full pass'))) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'CLEARER')!);
        }
        if (skillNames.some(s => s.includes('control training') || s === 'controlling')) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'CONTROL')!);
        }
        if (skillNames.some(s => s.includes('office training') || s === 'office')) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'OFFICE')!);
        }
        allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'WAREHOUSE')!);

        return allowed.length > 0 ? allowed : ALL_SHIFT_TYPES;
    };

    const selectedUser = users.find(u => u.id === formData.userId);
    const allowedShiftTypes = getShiftTypesForUser(selectedUser);

    // Auto-select first allowed shift type when user changes
    useEffect(() => {
        if (formData.userId && allowedShiftTypes.length > 0) {
            const currentTypeAllowed = allowedShiftTypes.some(t => t.value === formData.type);
            if (!currentTypeAllowed) {
                setFormData(prev => ({ ...prev, type: allowedShiftTypes[0].value }));
            }
        }
    }, [formData.userId]);

    return (
        <>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {isEditing ? 'Edit Shift' : 'Create Shift'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">
                        {error}
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded-lg text-sm border border-yellow-200">
                        <p className="font-semibold mb-1">Warnings:</p>
                        <ul className="list-disc list-inside">
                            {warnings.map((warning, idx) => (
                                <li key={idx}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {constraintViolations.length > 0 && (
                    <div className={`mb-4 p-3 rounded-lg text-sm border ${
                        overrideConstraints 
                            ? 'bg-orange-50 text-orange-700 border-orange-200' 
                            : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                        <p className="font-semibold mb-1">
                            {overrideConstraints ? 'Constraint Override Active' : 'Constraint Violations:'}
                        </p>
                        <ul className="list-disc list-inside mb-2">
                            {constraintViolations.map((violation, idx) => (
                                <li key={idx}>{violation}</li>
                            ))}
                        </ul>
                        {allowOverride && (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={overrideConstraints}
                                    onChange={(e) => setOverrideConstraints(e.target.checked)}
                                />
                                <span className="text-xs">Override constraints and proceed</span>
                            </label>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Staff Member *
                        </label>
                        {loadingUsers ? (
                            <div className="text-sm text-slate-500">Loading users...</div>
                        ) : (
                            <select
                                value={formData.userId}
                                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                                required
                                className="input-field"
                            >
                                <option value="">Select staff member</option>
                                {users.filter(u => u.role.name !== 'Admin' && u.role.name !== 'Super Admin').map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} ({user.role.name})
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div>
                        <DatePicker
                            label="Date *"
                            value={formData.date}
                            onChange={(v) => setFormData({ ...formData, date: v })}
                            placeholder="Pick shift date"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Start Time *
                            </label>
                            <input
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                required
                                className="input-field"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                End Time *
                            </label>
                            <input
                                type="time"
                                value={formData.endTime}
                                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                                required
                                className="input-field"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Shift Type *
                        </label>
                        {!formData.userId ? (
                            <div className="text-xs text-slate-400 py-2 px-3 bg-slate-50 rounded-xl border border-slate-200">Select a staff member first</div>
                        ) : (
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                required
                                className="input-field"
                            >
                                {allowedShiftTypes.map(st => (
                                    <option key={st.value} value={st.value}>{st.label}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Notes
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="input-field min-h-[80px]"
                            placeholder="Optional notes..."
                        />
                    </div>

                    <div className="flex gap-3 pt-4">
                        {isEditing && (
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="flex-1 btn-secondary bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                            >
                                Delete
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 btn-primary"
                        >
                            {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Shift')}
                        </button>
                    </div>
                </form>
            </div>
        </div>

            <ConfirmDialog
                open={showDeleteConfirm}
                title="Delete Shift"
                message="Are you sure you want to delete this shift?"
                confirmLabel="Delete"
                variant="danger"
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </>
    );
};

export default EditShiftModal;
